"use client";

import { getSession } from "next-auth/react";
import { getApiBaseUrl } from "@/lib/utils";
import { API_USER_ID, API_DB_ID } from "@/lib/api-constants";

const SESSION_PAYLOAD_TTL_MS = 2 * 60 * 1000; // 2 minutes
let cachedSessionPayload: Record<string, string> | null = null;
let cachedAt = 0;

/** Session fields merged into every direct backend request. Cached to avoid repeated /api/auth/session calls (e.g. when loading chat with many data_action/chart_commentary requests). */
async function getSessionPayload(): Promise<Record<string, string>> {
  const now = Date.now();
  if (
    cachedSessionPayload &&
    now - cachedAt < SESSION_PAYLOAD_TTL_MS
  ) {
    return cachedSessionPayload;
  }
  const session = await getSession();
  const user = session?.user as any;
  cachedSessionPayload = {
    user_id: API_USER_ID,
    db_id: API_DB_ID,
    session_id: user?.accessToken ?? "",
    username: user?.username ?? "",
  };
  cachedAt = now;
  return cachedSessionPayload;
}

/**
 * Call backend API directly (base URL from NEXT_PUBLIC_API_BASE_URL).
 * Merges user_id, session_id, db_id from session into the payload.
 */
export async function apiPost<T = unknown>(
  endpoint: string,
  data: Record<string, unknown> = {}
): Promise<T> {
  const sessionPayload = await getSessionPayload();
  const baseUrl = getApiBaseUrl();
  const path = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  const payload = { ...sessionPayload, ...data };

  const res = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((json as any)?.message || res.statusText || "Request failed");
  }
  return json as T;
}

/** Direct call to get_bot_response (legacy) */
export async function getChatResponse(data: {
  user_prompt: string;
  isRetry?: number;
  chat_id?: string | null;
}) {
  return apiPost("get_bot_response", {
    user_prompt: data.user_prompt,
    is_retry: data.isRetry ?? 0,
    chat_id: data.chat_id ?? undefined,
  });
}

/** Request body for agentic_get_bot_response API */
export interface AgenticGetBotResponseRequest {
  user_prompt: string;
  db_id: string;
  user_id: string;
  chat_id: string;
  is_retry: number;
  is_global_search: number;
}

/** Result object returned inside agentic_get_bot_response response */
export interface AgenticBotResponseResult {
  role?: string;
  title?: string;
  description?: string;
  type?: string;
  value?: number | string;
  sql_query_kpi?: string;
  total_usage?: { scope?: string; tokens_used?: number; prompt_tokens?: number; completion_tokens?: number; cost_usd?: number };
  id?: string;
  chat_id?: string;
  response_type?: number;
  chart_data?: any[];
  chart_type?: string;
  x_axis?: string;
  y_axis?: string;
  agentic_desc?: string;
  [key: string]: unknown;
}

/** Full response from agentic_get_bot_response API */
export interface AgenticGetBotResponseResponse {
  status_code: number;
  status?: string;
  message?: string;
  result?: AgenticBotResponseResult;
  sql_query?: string;
  table_data?: Record<string, string | number>[];
  total_usage?: { scope?: string; tokens_used?: number; prompt_tokens?: number; completion_tokens?: number; cost_usd?: number };
}

/** Direct call to agentic_get_bot_response (same shape as API). Sends user_id, db_id from session. */
export async function agenticGetBotResponse(data: {
  user_prompt: string;
  isRetry?: number;
  chat_id?: string | null;
  isGlobalSearch?: number | string;
}): Promise<AgenticGetBotResponseResponse> {
  return apiPost<AgenticGetBotResponseResponse>("agentic_get_bot_response", {
    user_prompt: data.user_prompt,
    is_retry: data.isRetry ?? 0,
    chat_id: data.chat_id ?? undefined,
    is_global_search: Number(data.isGlobalSearch ?? 0),
  });
}

/** Direct call to get_chat_list; returns same shape as /api/get-chat-list */
export async function getChatList(): Promise<{
  status_code: number;
  message: string;
  chats: any[];
}> {
  const raw = await apiPost<{ status_code?: number; result?: { chats?: any[] } }>(
    "get_chat_list",
    {}
  );
  const status_code = raw.status_code ?? 200;
  const chats = raw.result?.chats ?? [];
  return {
    status_code,
    message: status_code === 200 ? "Chat list fetched successfully" : "Network response was not ok",
    chats,
  };
}

/** Direct call to dashboard_list (same shape as /api/get-dashboard-list) */
export async function getDashboardList(): Promise<{
  success: boolean;
  status_code: number;
  message: string;
  dashboards: any[];
}> {
  const raw = await apiPost<{
    status_code?: number;
    result?: { dashboards?: any[] };
  }>("dashboard_list", {});
  const status_code = raw.status_code ?? 200;
  const dashboards = raw.result?.dashboards ?? [];
  return {
    success: status_code === 200,
    status_code,
    message:
      status_code === 200
        ? "Dashboard list fetched successfully"
        : "Network response was not ok",
    dashboards,
  };
}

// ---- Chat actions (direct API, no server actions) ----
export async function getSampleData(chat_id?: string) {
  return apiPost("get_sample_data", {
    chat_id: chat_id ?? undefined,
  });
}

export async function getQuerySuggestions(): Promise<{
  status_code?: number;
  result?: any[];
  message?: string;
}> {
  const raw = await apiPost<{
    status_code?: number;
    result?: any[];
    suggested_questions?: any[];
  }>("get_querybanksuggestions", {});
  return {
    ...raw,
    result: raw.result ?? raw.suggested_questions ?? [],
  };
}

export async function createQueryBank(data: { db_id: string }) {
  return apiPost("create_querybank", {
    db_id: data.db_id,
  });
}

export async function getSuggestView(data: { db_id: string | number }) {
  return apiPost("get_suggestedviews", {
    db_id: data.db_id,
  });
}

export async function getAISuggestedQuestions(): Promise<{
  status_code: number;
  message?: string;
  questions: any[];
}> {
  try {
    const raw = await apiPost<{
      status_code?: number;
      result?: { questions?: any[] };
      message?: string;
    }>("get_ai_suggested_questions", {});
    const status_code = raw.status_code ?? 200;
    const questions = Array.isArray(raw.result?.questions) ? raw.result.questions : [];
    return {
      status_code,
      message: raw.message,
      questions,
    };
  } catch {
    return { status_code: 500, message: "Failed to fetch suggested questions", questions: [] };
  }
}

export async function getUserCreditDetails() {
  return apiPost("user_credits", {});
}

export async function getConnectedDb() {
  return apiPost("get_connected_db", {});
}

export async function createChat() {
  return apiPost("create_chat", {});
}

export async function createNewChat(): Promise<{
  status_code: number;
  message?: string;
  chatId?: string;
}> {
  const raw = await apiPost<{
    status_code?: number;
    result?: { chat_id?: string };
    message?: string;
  }>("create_chat", {});
  if (!raw.result?.chat_id)
    return {
      status_code: 500,
      message: "Chat creation failed, no chat_id returned",
    };
  if (raw.status_code === 400)
    return {
      status_code: 400,
      message: raw.message || "New chat creation failed due to existing chat",
      chatId: raw.result.chat_id,
    };
  return {
    status_code: 200,
    message: "New chat created successfully",
    chatId: raw.result.chat_id,
  };
}

export async function getLatestChat() {
  return apiPost<{ status_code?: number; result?: any; message?: string }>(
    "get_latest_chat",
    {}
  );
}

export async function getChatById(chat_id: string) {
  return apiPost<{ status_code?: number; result?: { chat_history?: any[] }; message?: string }>(
    "get_chat_by_id",
    { chat_id }
  );
}

export async function updateChat(data: { chat_id: string; chat_data: any[] }) {
  return apiPost("update_chat", {
    chat_id: data.chat_id,
    chat_data: data.chat_data,
  });
}

export async function getChatParams() {
  return apiPost("get_chatid_list", {});
}

export async function deleteChatById(id: string) {
  return apiPost("hide_chat", { chat_id: id });
}

/** Response item from get_questions API */
export interface GetQuestionsItem {
  q_id: string;
  question: string;
}

/** get_questions API response: POST with { user_id, db_id } */
export async function getSuggestedTemplateQuestion(): Promise<{
  status_code: number;
  message?: string;
  questions: GetQuestionsItem[];
}> {
  try {
    const raw = await apiPost<{
      status_code?: number;
      status?: string;
      result?: GetQuestionsItem[];
      message?: string;
    }>("get_questions", {
      user_id: API_USER_ID,
      db_id: API_DB_ID,
    });
    const status_code = raw.status_code ?? 200;
    const questions = Array.isArray(raw.result) ? raw.result : [];
    return {
      status_code,
      message: raw.message,
      questions,
    };
  } catch {
    return { status_code: 500, message: "Unexpected error occurred", questions: [] };
  }
}

export async function getSuggestedTemplates(data: { u_id: string; chat_id?: string }) {
  return apiPost("fetch_answer_and_update_chat", {
    u_id: data.u_id,
    chat_id: data.chat_id ?? undefined,
  });
}

export async function getChatLiked(data: { chat_id: string; obj_id: string; is_liked: boolean }) {
  return apiPost("like_chat", {
    chat_id: data.chat_id,
    obj_id: data.obj_id,
    is_liked: data.is_liked,
  });
}

export async function getColumnsDescription() {
  return apiPost<{ status_code?: number; result?: any; message?: string }>(
    "get_column_description",
    {
      user_id: API_USER_ID,
      db_id: API_DB_ID,
    }
  );
}

export async function updateColumns(data: {
  updated_table_description: string;
  columns: { column_name: string; data_type: string; column_description: string }[];
}) {
  return apiPost("update_column_description", {
    updated_table_description: data.updated_table_description,
    columns_data: data.columns,
  });
}

export async function updateChartData(data: any) {
  return apiPost("update_chat_item", data);
}

export async function getChartComment(data: {
  chat_id: string;
  user_query: string;
  bot_response_id: string;
  user_prompt?: string;
}) {
  return apiPost<{ status_code?: number; result?: { commentary?: any }; message?: string }>(
    "chart_commentary",
    {
      chat_id: data.chat_id,
      user_query: data.user_query,
      bot_response_id: data.bot_response_id,
    }
  );
}

export async function getActionContent(data: {
  bot_response_id: string;
  user_query: string;
  chat_id: string;
  user_prompt?: string;
}) {
  return apiPost<{ status_code?: number; result?: any; message?: string }>("data_action", {
    bot_response_id: data.bot_response_id,
    user_query: data.user_query,
    chat_id: data.chat_id,
    user_prompt: data.user_prompt,
  });
}

// ---- Dashboard actions (direct API) ----
export async function getDashboards() {
  const raw = await apiPost<{ result?: { dashboards?: any[] } }>("get_dashboards", {});
  return raw.result?.dashboards ?? [];
}

export async function createNewDashboard(data: {
  dashboard_id: string;
  dashboard_name: string;
  dashboard_desc: string;
}) {
  return apiPost("create_dashboard", {
    dashboard_id: data.dashboard_id,
    dashboard_name: data.dashboard_name,
    dashboard_desc: data.dashboard_desc,
  });
}

export async function getSingleDashboard(dash_slug: string) {
  const raw = await apiPost<{ result?: any }>("get_dashboard", {
    dash_id: "",
    dash_slug: dash_slug,
  });
  return raw.result ?? null;
}

const TOTAL_COLUMNS = 3;
const ITEM_WIDTH = 6;
const ITEM_HEIGHT = 20;

function calculatePosition(numItems: number) {
  const rowIndex = Math.floor(numItems / TOTAL_COLUMNS);
  const colIndex = numItems % TOTAL_COLUMNS;
  return { x: colIndex * ITEM_WIDTH, y: rowIndex * ITEM_HEIGHT };
}

function createDashboardItem(data: any, position: { x: number; y: number }) {
  return {
    user_id: data.user_id,
    session_id: data.session_id,
    dashboard_id: data.dash_id,
    dashboard_slug: data.dash_slug,
    dash_items: {
      id: data.dash_items.id,
      i: data.dash_items.id,
      x: position.x,
      y: position.y,
      w: data.dash_items.type === "kpi" ? 3 : ITEM_WIDTH,
      h: data.dash_items.type === "kpi" ? 6 : ITEM_HEIGHT,
      title: data.dash_items.title,
      desc: data.dash_items.desc,
      type: data.dash_items.type,
      value: data.dash_items.value,
      xAxis_key: data.dash_items.xAxis_key,
      yAxis_key: data.dash_items.yAxis_key,
      xAxis_label: data.dash_items.xAxis_label,
      yAxis_label: data.dash_items.yAxis_label,
      chart_type: data.dash_items.chart_type,
      item_data: data.dash_items.item_data,
      heading: data.dash_items.heading,
      description: data.dash_items.description,
    },
  };
}

export async function addItemToDashboard(data: any) {
  const sessionPayload = await getSessionPayload();
  const existing = await getSingleDashboard(data.dash_slug);
  const dashItems = existing?.dash_items || [];
  const position = calculatePosition(dashItems.length);
  const newItem = createDashboardItem(
    { ...sessionPayload, ...data, session_id: sessionPayload.session_id },
    position
  );
  return apiPost<{ status_code?: number }>("addto_dashboard", newItem);
}

export async function updateDashboard(newData: any) {
  return apiPost("dashboard_update", {
    dashboard_id: newData.dash_id,
    updated_dashboard: newData,
  });
}

export async function deleteDashboard(id: string) {
  return apiPost("dashboard_delete", { dashboard_id: id });
}

export async function deleteComponent(id: string) {
  return apiPost("delete_component", { component_id: id });
}

export async function getDashboardInsights(dash_id: string) {
  return apiPost("get_combined_insights", { dashboard_id: dash_id });
}

/** Paraphrase has no auth - plain POST */
export async function rephraseSelectedInsight(text: string) {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}paraphrase`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as any)?.message || res.statusText);
  return json;
}

// ---- Index / user help (direct API) ----
export async function sendUserQuery(userQuery: { query: string }) {
  return apiPost("user_help", {
    user_email: (await getSessionPayload()).username,
    user_query: userQuery.query,
  });
}

// ---- Auth (direct API; login/logout stay server-side via NextAuth) ----
/** Register – no session required */
export async function doRegistration(data: {
  first_name: string;
  last_name: string;
  username: string;
  phone_number: string;
  state: string;
  city: string;
  organization: string;
  role: string;
  password: string;
}) {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok)
    return {
      status_code: 500,
      message: (json as any)?.message || "Oops! Something went wrong. Please try again",
    };
  return json;
}

// ---- Profile (direct API) ----
export async function getUserDetails() {
  const raw = await apiPost<{ status_code?: number; user_details?: any }>(
    "get_userdetails",
    {}
  );
  return raw.status_code === 200 ? raw.user_details : undefined;
}

export async function updateUserProfile(data: any) {
  return apiPost("update_user", data);
}

export interface PostgreConnectionProps {
  host: string;
  database: string;
  port: number;
  username: string;
  password: string;
  db_type?: string;
  db_id?: string;
}

export async function checkPostgreConenction(credentials: PostgreConnectionProps) {
  const session = await getSessionPayload();
  return apiPost("check_connection", { ...credentials, user_id: session.user_id });
}

export async function connectPostgreConenction(credentials: PostgreConnectionProps) {
  const session = await getSessionPayload();
  return apiPost("get_schemas", { ...credentials, user_id: session.user_id });
}

export async function getDatabaseID(credentials: PostgreConnectionProps) {
  const session = await getSessionPayload();
  return apiPost("update_database_connection", {
    ...credentials,
    user_id: session.user_id,
    session_id: session.session_id,
  });
}
